/**
 * Reusable Team Management wizard step components.
 *
 * Each is a CONTROLLED, presentational component, it renders the step
 * UI and reports changes via onChange. Persistence is the
 * orchestrator's job (TeamWizard for per-team; the future new-club
 * wizard for bulk). That separation is what makes these reusable: the
 * new-club wizard imports CoachesStep + SquadStep directly and drives
 * them per team.
 *
 * See docs/features/team-management-wizard.md.
 */
import { useState } from 'react';
import { Coach } from '../../api/client';
import ImageUploader from '../ImageUploader';

export function genCoachId(): string {
  return `coach-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
}

/* ── Coaches step ─────────────────────────────────────────────── */

export function CoachesStep({
  coaches,
  onChange,
  onUploadingChange,
}: {
  coaches: Coach[];
  onChange: (next: Coach[]) => void;
  /** Reports true while ANY coach photo is uploading, so the
   *  orchestrator can block "Next" (same race as the team photo). */
  onUploadingChange?: (uploading: boolean) => void;
}) {
  // Track which coach photos are mid-upload; report aggregate up.
  // Only the setter is used (functional updates), no direct read.
  const [, setUploading] = useState<Record<string, boolean>>({});
  const setCoachUploading = (id: string, up: boolean) => {
    setUploading(prev => {
      const next = { ...prev, [id]: up };
      onUploadingChange?.(Object.values(next).some(Boolean));
      return next;
    });
  };

  const add = () => onChange([...coaches, { id: genCoachId(), name: '', image: '' }]);
  const remove = (id: string) => onChange(coaches.filter(c => c.id !== id));
  const update = (id: string, field: keyof Coach, value: string) =>
    onChange(coaches.map(c => (c.id === id ? { ...c, [field]: value } : c)));

  return (
    <div className="tmw-step-body">
      <p className="tmw-step-hint">
        Add the team's coaches. You can skip this and add them later.
      </p>

      {coaches.length === 0 && (
        <p className="tmw-empty-hint">No coaches yet.</p>
      )}

      <div className="tmw-coaches-list">
        {coaches.map((coach, i) => (
          <div key={coach.id} className="tmw-coach-row">
            <div className="tmw-coach-avatar">
              {coach.image ? (
                <div className="tmw-coach-avatar-preview">
                  <img src={coach.image} alt={coach.name} />
                  <button
                    type="button"
                    className="tmw-coach-avatar-remove"
                    onClick={() => update(coach.id, 'image', '')}
                    aria-label="Remove photo"
                  >
                    ×
                  </button>
                </div>
              ) : (
                <div className="tmw-coach-avatar-empty">
                  <ImageUploader
                    onUpload={(url) => update(coach.id, 'image', url)}
                    onUploadingChange={(up) => setCoachUploading(coach.id, up)}
                    label="Photo"
                  />
                </div>
              )}
            </div>
            <input
              type="text"
              className="tmw-input"
              placeholder={`Coach ${i + 1} name`}
              value={coach.name}
              onChange={e => update(coach.id, 'name', e.target.value)}
            />
            <button
              type="button"
              className="ebtn ebtn-ghost ebtn-sm"
              onClick={() => remove(coach.id)}
              aria-label="Remove coach"
            >
              Remove
            </button>
          </div>
        ))}
      </div>

      <button type="button" className="ebtn ebtn-secondary ebtn-sm" onClick={add}>
        + Add coach
      </button>
    </div>
  );
}

/* ── Squad step ───────────────────────────────────────────────── */

export interface ParsedSquadRow {
  firstName: string;
  lastName: string;
  guardianName: string;
  guardianEmail: string;
}

/**
 * Parse pasted squad text, one player per non-blank line. Comma-separated:
 *   Player Name, Guardian Name, Guardian Email
 * Guardian fields are optional. First token of the player part is the
 * first name, the rest the surname.
 */
export function parseSquadInput(text: string): ParsedSquadRow[] {
  return text
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean)
    .map(line => {
      const [namePart = '', guardianName = '', guardianEmail = ''] =
        line.split(',').map(s => s.trim());
      const parts = namePart.split(/\s+/);
      const firstName = parts.shift() || '';
      const lastName = parts.join(' ');
      return { firstName, lastName, guardianName, guardianEmail };
    })
    .filter(p => p.firstName.length > 0);
}

export function SquadStep({
  value,
  onChange,
  teamName,
}: {
  value: string;
  onChange: (next: string) => void;
  teamName?: string;
}) {
  const parsed = parseSquadInput(value);
  const withGuardian = parsed.filter(p => p.guardianEmail).length;

  return (
    <div className="tmw-step-body">
      <p className="tmw-step-hint">
        Paste your squad, one player per line. Optionally add a guardian
        after the name so you can email parents later:
        <br />
        <code>Player name, Guardian name, Guardian email</code>
        <br />
        Guardian details are optional. You can skip this whole step and add
        players from the Players tab.
      </p>

      <textarea
        className="tmw-squad-textarea"
        rows={10}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={`Tommy Smith, Jane Smith, jane@example.com\nSophie Jones, Mark Jones, mark@example.com\nJake Brown\n…`}
        aria-label="Squad, one player per line, optional guardian"
      />

      <div className="tmw-squad-preview">
        {parsed.length === 0 ? (
          <span className="tmw-empty-hint">No players entered yet.</span>
        ) : (
          <span>
            <strong>{parsed.length}</strong> player{parsed.length === 1 ? '' : 's'} will be
            added{teamName ? ` to ${teamName}` : ''}
            {withGuardian > 0 && <> · <strong>{withGuardian}</strong> with a guardian email</>}
            {' '}, private by default (you control public visibility per player later).
          </span>
        )}
      </div>
    </div>
  );
}
